import { useState, useEffect, useRef, useMemo } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, TrendingUp, AlertTriangle, Target, Award, Star,
  ArrowUpRight, Eye, BarChart3, Brain, Shield, Trophy,
  ChevronRight, ArrowUp, ArrowDown, Minus, FileText, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClubDashboard = () => {
  const { club, filters } = useOutletContext();
  const [data, setData] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const trendCanvasRef = useRef(null);

  useEffect(() => {
    if (club?.id) fetchAll();
  }, [club, filters]);

  useEffect(() => {
    if (matchData?.recent_results?.length > 1) drawTrendChart();
  }, [matchData]);

  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (filters?.season) params.append('season', filters.season);
    if (filters?.team_id) params.append('team_id', filters.team_id);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  const fetchAll = async () => {
    const token = localStorage.getItem('club_token');
    const headers = { Authorization: `Bearer ${token}` };
    const qs = buildFilterParams();
    try {
      const [dashRes, matchRes] = await Promise.all([
        axios.get(`${API}/clubs/intelligence/dashboard${qs}`, { headers }),
        axios.get(`${API}/clubs/intelligence/match-insights${qs}`, { headers }).catch(() => ({ data: null }))
      ]);
      setData(dashRes.data);
      setMatchData(matchRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const drawTrendChart = () => {
    const canvas = trendCanvasRef.current;
    if (!canvas || !matchData?.recent_results?.length) return;

    const ctx = canvas.getContext('2d');
    const dpr = 2;
    const w = canvas.parentElement.offsetWidth;
    const h = 160;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const results = [...matchData.recent_results].reverse();
    const scores = results.map(r => r.avg_player_score || 5);
    const padding = { top: 20, right: 15, bottom: 40, left: 35 };
    const cw = w - padding.left - padding.right;
    const ch = h - padding.top - padding.bottom;
    const minV = Math.max(0, Math.min(...scores) - 1);
    const maxV = Math.min(10, Math.max(...scores) + 1);
    const range = maxV - minV || 1;

    // Grid lines + Y axis labels
    for (let i = 0; i <= 4; i++) {
      const y = padding.top + (ch * i) / 4;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(w - padding.right, y);
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
      ctx.font = '9px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.textAlign = 'right';
      ctx.fillText((maxV - (range * i) / 4).toFixed(1), padding.left - 5, y + 3);
    }

    // Line
    ctx.beginPath();
    scores.forEach((v, i) => {
      const x = padding.left + (cw * i) / Math.max(scores.length - 1, 1);
      const y = padding.top + ch - ((v - minV) / range) * ch;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#d4af37';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Fill under line
    const lastX = padding.left + (cw * (scores.length - 1)) / Math.max(scores.length - 1, 1);
    ctx.lineTo(lastX, padding.top + ch);
    ctx.lineTo(padding.left, padding.top + ch);
    ctx.closePath();
    const grad = ctx.createLinearGradient(0, padding.top, 0, padding.top + ch);
    grad.addColorStop(0, 'rgba(212,175,55,0.15)');
    grad.addColorStop(1, 'rgba(212,175,55,0.01)');
    ctx.fillStyle = grad;
    ctx.fill();

    // Dots + X-axis labels (opponents)
    scores.forEach((v, i) => {
      const x = padding.left + (cw * i) / Math.max(scores.length - 1, 1);
      const y = padding.top + ch - ((v - minV) / range) * ch;
      ctx.beginPath();
      ctx.arc(x, y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = '#d4af37';
      ctx.fill();

      // Score label above dot
      ctx.font = 'bold 9px sans-serif';
      ctx.fillStyle = '#d4af37';
      ctx.textAlign = 'center';
      ctx.fillText(v.toFixed(1), x, y - 8);

      // Opponent name on X-axis
      ctx.font = '8px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.textAlign = 'center';
      const opponent = results[i]?.opponent || '';
      const shortOpp = opponent.length > 8 ? opponent.slice(0, 7) + '..' : opponent;
      ctx.fillText(shortOpp, x, padding.top + ch + 14);
    });

    // Y-axis title
    ctx.save();
    ctx.translate(8, padding.top + ch / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.font = '8px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.textAlign = 'center';
    ctx.fillText('Avg Score', 0, 0);
    ctx.restore();
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return null;

  const dist = data.level_distribution || {};
  const total = data.total_athletes || 0;
  const totalEval = data.total_evaluated || 0;
  const avgScore = totalEval > 0 ? Math.round((data.top_performers || []).reduce((s, p) => s + p.overall_score, 0) / Math.min(totalEval, (data.top_performers || []).length || 1)) : 0;
  const avgPct = Math.round((avgScore / 10) * 100);
  const actionCount = (dist.focus || 0) + (matchData?.flagged_players?.length || 0);
  const matchRecord = matchData?.record || { wins: 0, draws: 0, losses: 0 };

  return (
    <div className="space-y-4" data-testid="club-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase tracking-wide">
            Performance <span className="text-primary">Intelligence</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">{club?.name} — Command Center</p>
        </div>
        <Link to={`/club/${club?.id}/match-report`}>
          <Button className="bg-primary text-black hover:bg-primary/90 gap-2" data-testid="new-match-btn">
            <FileText size={16} /> Match Report
          </Button>
        </Link>
      </div>

      {/* ROW 1: KPI CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Total Athletes */}
        <Card delay={0}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-sm bg-primary/15 border border-primary/20 flex items-center justify-center">
              <Users size={16} className="text-primary" />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Athletes</span>
          </div>
          <p className="font-heading text-3xl text-primary">{total}</p>
          <p className="text-[10px] text-muted-foreground mt-1">{totalEval} evaluated</p>
        </Card>

        {/* Player Distribution */}
        <Card delay={0.05} className="col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <Target size={14} className="text-primary" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Player Distribution</span>
          </div>
          <div className="space-y-2">
            {[
              { key: 'focus', label: 'Focus', count: dist.focus || 0, color: 'bg-red-500' },
              { key: 'developing', label: 'Developing', count: dist.developing || 0, color: 'bg-amber-500' },
              { key: 'proficient', label: 'Proficient', count: dist.proficient || 0, color: 'bg-emerald-500' }
            ].map(d => (
              <div key={d.key} className="flex items-center gap-2">
                <span className="text-[10px] w-16 text-muted-foreground">{d.label}</span>
                <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${total > 0 ? (d.count / total) * 100 : 0}%` }}
                    transition={{ duration: 0.6 }} className={`h-full rounded-full ${d.color}`} />
                </div>
                <span className="text-xs font-mono w-6 text-right text-primary">{d.count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Avg Club Performance */}
        <Card delay={0.1}>
          <div className="flex items-center gap-2 mb-2">
            <Star size={14} className="text-primary" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Avg Performance</span>
          </div>
          <div className="flex items-end gap-2">
            <span className="font-heading text-3xl text-primary">{avgPct}</span>
            <span className="text-muted-foreground text-sm mb-1">/100</span>
          </div>
          {matchData?.trend && matchData.trend !== 'neutral' && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${matchData.trend === 'improving' ? 'text-emerald-400' : 'text-red-400'}`}>
              {matchData.trend === 'improving' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              {matchData.trend === 'improving' ? 'Improving' : 'Declining'}
            </div>
          )}
        </Card>

        {/* Action Required - Now with player names */}
        <Card delay={0.15}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-red-400" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Action Required</span>
          </div>
          <div className="space-y-2">
            {(data.needs_attention || []).slice(0, 3).map(a => (
              <Link key={a.athlete_id} to={`/club/${club?.id}/intelligence/player/${a.athlete_id}`}
                className="flex items-center gap-2 text-xs hover:text-primary transition-colors group"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                <span className="text-muted-foreground group-hover:text-primary truncate">{a.name}</span>
                <span className="text-[9px] text-red-400 ml-auto shrink-0">{a.overall_score}</span>
              </Link>
            ))}
            {(matchData?.flagged_players || []).slice(0, 2).map((p, i) => (
              <Link key={`flag-${i}`} to={`/club/${club?.id}/intelligence/player/${p.athlete_id}`}
                className="flex items-center gap-2 text-xs hover:text-primary transition-colors group"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                <span className="text-muted-foreground group-hover:text-primary truncate">{p.name}</span>
                <span className="text-[9px] text-amber-400 ml-auto shrink-0">{p.flag.replace(/_/g, ' ')}</span>
              </Link>
            ))}
            {actionCount === 0 && !data.alerts?.length && (
              <p className="text-xs text-emerald-400 flex items-center gap-1"><Shield size={12} /> All clear</p>
            )}
          </div>
        </Card>
      </div>

      {/* ROW 2: CATEGORY + MATCHES + TREND */}
      <div className="grid lg:grid-cols-3 gap-3">
        {/* Category Overview - Fixed with proper avg score + count */}
        <Card delay={0.2}>
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <BarChart3 size={13} className="text-primary" /> Category Overview
          </h2>
          <div className="space-y-3">
            {(data.age_group_performance || []).map(ag => (
              <div key={ag.age_group}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-muted-foreground">{ag.age_group}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-mono ${ag.avg_score >= 8.1 ? 'text-emerald-400' : ag.avg_score >= 6.1 ? 'text-amber-400' : ag.avg_score > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                      {ag.avg_score > 0 ? ag.avg_score : '—'}
                    </span>
                    <span className="text-[10px] text-zinc-500">({ag.count})</span>
                  </div>
                </div>
                <div className="h-2 bg-zinc-800 rounded-sm overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${ag.avg_score > 0 ? (ag.avg_score / 10) * 100 : 0}%` }}
                    transition={{ duration: 0.6 }} className={`h-full rounded-sm ${
                      ag.avg_score >= 8.1 ? 'bg-emerald-500' : ag.avg_score >= 6.1 ? 'bg-amber-500' : ag.avg_score > 0 ? 'bg-red-500' : 'bg-zinc-700'
                    }`} />
                </div>
              </div>
            ))}
            {!data.age_group_performance?.length && (data.teams_summary || []).map(t => (
              <Link key={t.team_id} to={`/club/${club?.id}/teams/${t.team_id}`}
                className="flex items-center gap-3 hover:bg-zinc-800/30 rounded-sm p-1 transition-colors"
              >
                <span className="text-xs w-20 truncate text-muted-foreground">{t.team_name}</span>
                <div className="flex-1 h-2 bg-zinc-800 rounded-sm overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${(t.avg_score / 10) * 100}%` }}
                    transition={{ duration: 0.6 }} className="h-full rounded-sm bg-primary/70" />
                </div>
                <span className="text-xs font-mono w-6 text-right text-primary">{t.avg_score || '—'}</span>
              </Link>
            ))}
          </div>
        </Card>

        {/* Recent Match Results - Clickable */}
        <Card delay={0.25}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Trophy size={13} className="text-primary" /> Recent Match Results
            </h2>
            {matchData?.total_matches > 0 && (
              <Link to={`/club/${club?.id}/match-history`} className="text-[10px] text-primary flex items-center gap-0.5 hover:underline">
                View All <ArrowUpRight size={10} />
              </Link>
            )}
          </div>
          {matchData?.recent_results?.length > 0 ? (
            <div className="space-y-2">
              {matchData.recent_results.slice(0, 4).map(m => (
                <Link key={m.match_id} to={`/club/${club?.id}/match-history/${m.match_id}`}
                  className="flex items-center justify-between p-2 bg-zinc-900/50 rounded-sm border border-border/10 hover:border-primary/20 transition-colors group"
                  data-testid={`match-result-${m.match_id}`}
                >
                  <div>
                    <p className="text-sm group-hover:text-primary transition-colors">{m.team_name}</p>
                    <p className="text-[10px] text-muted-foreground">vs {m.opponent}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-heading text-lg">{m.our_score} : {m.opponent_score}</span>
                    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${
                      m.result === 'win' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' :
                      m.result === 'loss' ? 'bg-red-500/15 text-red-400 border-red-500/20' :
                      'bg-amber-500/15 text-amber-400 border-amber-500/20'
                    }`}>{m.result}</span>
                  </div>
                </Link>
              ))}
              {matchData.total_matches > 0 && (
                <div className="flex gap-3 pt-2 border-t border-border/20 text-xs">
                  <span className="text-emerald-400">{matchRecord.wins}W</span>
                  <span className="text-amber-400">{matchRecord.draws}D</span>
                  <span className="text-red-400">{matchRecord.losses}L</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <Trophy size={24} className="mx-auto text-zinc-700 mb-2" />
              <p className="text-xs text-muted-foreground">No matches reported yet</p>
              <Link to={`/club/${club?.id}/match-report`} className="text-xs text-primary hover:underline mt-1 inline-block">
                Submit first match report
              </Link>
            </div>
          )}
        </Card>

        {/* Performance Trend - Improved with labels & context */}
        <Card delay={0.3}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <TrendingUp size={13} className="text-primary" /> Match Performance Trend
            </h2>
            {matchData?.trend && matchData.trend !== 'neutral' && (
              <span className={`text-[10px] flex items-center gap-1 ${matchData.trend === 'improving' ? 'text-emerald-400' : 'text-red-400'}`}>
                {matchData.trend === 'improving' ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                {matchData.trend === 'improving' ? 'Improving' : 'Declining'}
              </span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mb-2">
            Average player score per match (last {matchData?.recent_results?.length || 0} matches)
          </p>
          {matchData?.recent_results?.length > 1 ? (
            <div className="w-full">
              <canvas ref={trendCanvasRef} />
            </div>
          ) : (
            <div className="flex items-center justify-center h-24">
              <p className="text-xs text-muted-foreground">Need more match data for trends</p>
            </div>
          )}
        </Card>
      </div>

      {/* ROW 3: TOP PERFORMERS + INSIGHTS + DECISIONS */}
      <div className="grid lg:grid-cols-3 gap-3">
        {/* Top Performers - Clickable */}
        <Card delay={0.35}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Award size={13} className="text-emerald-400" /> Top Performers
            </h2>
            <Link to={`/club/${club?.id}/intelligence`} className="text-[10px] text-primary flex items-center gap-0.5 hover:underline">
              View All <ArrowUpRight size={10} />
            </Link>
          </div>
          <div className="space-y-2.5">
            {(data.top_performers || []).slice(0, 5).map((a, i) => (
              <Link key={a.athlete_id} to={`/club/${club?.id}/intelligence/player/${a.athlete_id}`}
                className="flex items-center gap-3 group hover:bg-zinc-800/30 p-1.5 rounded-sm transition-colors"
              >
                <span className="text-[10px] font-mono text-zinc-500 w-4">{i + 1}</span>
                <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-primary">
                  {a.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate group-hover:text-primary transition-colors">{a.name}</p>
                  <p className="text-[10px] text-muted-foreground">{a.position || 'Player'} · {a.team_name}</p>
                </div>
                <span className={`font-heading text-sm ${a.overall_score >= 8.1 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {a.overall_score}
                </span>
              </Link>
            ))}
            {!data.top_performers?.length && (
              <p className="text-xs text-muted-foreground text-center py-4">No evaluated athletes</p>
            )}
          </div>
        </Card>

        {/* Critical Insights - Clickable */}
        <Card delay={0.4}>
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Brain size={13} className="text-primary" /> Critical Insights
          </h2>
          <div className="space-y-2.5">
            {matchData?.common_issues?.length > 0 && matchData.common_issues.map((ci, i) => (
              <div key={i} className="flex items-start gap-2.5 p-2 bg-red-500/5 border border-red-500/10 rounded-sm">
                <AlertTriangle size={12} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs">{ci.issue.replace(/_/g, ' ')} detected</p>
                  <p className="text-[10px] text-muted-foreground">Flagged in {ci.count} recent match(es)</p>
                </div>
              </div>
            ))}
            {(data.needs_attention || []).slice(0, 3).map(a => (
              <Link key={a.athlete_id} to={`/club/${club?.id}/intelligence/player/${a.athlete_id}`}
                className="flex items-start gap-2.5 p-2 bg-amber-500/5 border border-amber-500/10 rounded-sm hover:border-amber-500/30 transition-colors group"
              >
                <Target size={12} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs group-hover:text-amber-300">{a.name} needs development focus</p>
                  <p className="text-[10px] text-muted-foreground">Score: {a.overall_score} · {a.position || 'Player'}</p>
                </div>
              </Link>
            ))}
            {(matchData?.flagged_players || []).slice(0, 3).map((p, i) => (
              <Link key={`fp-${i}`} to={`/club/${club?.id}/intelligence/player/${p.athlete_id}`}
                className="flex items-start gap-2.5 p-2 bg-amber-500/5 border border-amber-500/10 rounded-sm hover:border-amber-500/30 transition-colors group"
              >
                <Zap size={12} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs group-hover:text-amber-300">{p.name}</p>
                  <p className="text-[10px] text-muted-foreground">{p.flag.replace(/_/g, ' ')} — vs {p.match}</p>
                </div>
              </Link>
            ))}
            {!matchData?.common_issues?.length && !data.needs_attention?.length && !matchData?.flagged_players?.length && (
              <div className="text-center py-4">
                <Shield size={20} className="mx-auto text-emerald-400/30 mb-1" />
                <p className="text-xs text-muted-foreground">No critical insights</p>
              </div>
            )}
          </div>
        </Card>

        {/* Recommended Actions - Clickable */}
        <Card delay={0.45}>
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Zap size={13} className="text-primary" /> Recommended Actions
          </h2>
          <div className="space-y-2.5">
            {(data.top_performers || []).filter(a => a.overall_score >= 8.5).slice(0, 2).map(a => (
              <Link key={a.athlete_id} to={`/club/${club?.id}/intelligence/player/${a.athlete_id}`}
                className="flex items-start gap-2.5 p-2 bg-emerald-500/5 border border-emerald-500/10 rounded-sm hover:border-emerald-500/30 transition-colors group"
              >
                <ArrowUp size={12} className="text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-emerald-400 group-hover:text-emerald-300">Ready for Promotion</p>
                  <p className="text-[10px] text-muted-foreground">{a.name} — {a.overall_score} overall</p>
                </div>
              </Link>
            ))}
            {(data.needs_attention || []).slice(0, 2).map(a => (
              <Link key={a.athlete_id} to={`/club/${club?.id}/intelligence/player/${a.athlete_id}`}
                className="flex items-start gap-2.5 p-2 bg-red-500/5 border border-red-500/10 rounded-sm hover:border-red-500/30 transition-colors group"
              >
                <Target size={12} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-red-400 group-hover:text-red-300">Focus Training Needed</p>
                  <p className="text-[10px] text-muted-foreground">{a.name} — {a.overall_score} overall</p>
                </div>
              </Link>
            ))}
            {matchData?.common_issues?.length > 0 && (
              <div className="flex items-start gap-2.5 p-2 bg-amber-500/5 border border-amber-500/10 rounded-sm">
                <Brain size={12} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-amber-400">Training Focus</p>
                  <p className="text-[10px] text-muted-foreground">
                    Address {matchData.common_issues[0].issue.replace(/_/g, ' ')} in upcoming sessions
                  </p>
                </div>
              </div>
            )}
            {!data.top_performers?.filter(a => a.overall_score >= 8.5).length && !data.needs_attention?.length && (
              <p className="text-xs text-muted-foreground text-center py-4">Submit match reports for AI recommendations</p>
            )}
          </div>
        </Card>
      </div>

      {/* ROW 3.5: SCORING LEADERS - All Clickable */}
      {(matchData?.top_scorers?.length > 0 || matchData?.assist_leaders?.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-3">
          <Card delay={0.47}>
            <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
              <Trophy size={13} className="text-primary" /> Top Scorers
            </h2>
            <div className="space-y-2">
              {(matchData?.top_scorers || []).map((s, i) => (
                <Link key={i} to={`/club/${club?.id}/intelligence/player/${s.athlete_id}`}
                  className="flex items-center gap-3 p-2 bg-zinc-900/40 rounded-sm border border-border/10 hover:border-primary/20 transition-colors group"
                >
                  <span className="text-[10px] font-mono text-primary w-4">{i + 1}</span>
                  <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-[9px] font-bold text-primary">
                    {s.name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate group-hover:text-primary transition-colors">{s.name}</p>
                    <p className="text-[10px] text-muted-foreground">{s.matches} match(es)</p>
                  </div>
                  <span className="font-heading text-lg text-primary">{s.goals}</span>
                  <span className="text-[10px] text-muted-foreground">goals</span>
                </Link>
              ))}
            </div>
          </Card>

          <Card delay={0.49}>
            <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
              <Target size={13} className="text-emerald-400" /> Assist Leaders
            </h2>
            <div className="space-y-2">
              {(matchData?.assist_leaders || []).map((s, i) => (
                <Link key={i} to={`/club/${club?.id}/intelligence/player/${s.athlete_id}`}
                  className="flex items-center gap-3 p-2 bg-zinc-900/40 rounded-sm border border-border/10 hover:border-emerald-500/20 transition-colors group"
                >
                  <span className="text-[10px] font-mono text-emerald-400 w-4">{i + 1}</span>
                  <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-[9px] font-bold text-emerald-400">
                    {s.name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate group-hover:text-emerald-400 transition-colors">{s.name}</p>
                    <p className="text-[10px] text-muted-foreground">{s.matches} match(es)</p>
                  </div>
                  <span className="font-heading text-lg text-emerald-400">{s.assists}</span>
                  <span className="text-[10px] text-muted-foreground">assists</span>
                </Link>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Defensive Standouts - Clickable */}
      {matchData?.defensive_standouts?.length > 0 && (
        <Card delay={0.51}>
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Shield size={13} className="text-blue-400" /> Defensive Standouts
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {matchData.defensive_standouts.map((d, i) => (
              <Link key={i} to={`/club/${club?.id}/intelligence/player/${d.athlete_id}`}
                className="flex items-center gap-3 p-2.5 bg-zinc-900/40 rounded-sm border border-blue-500/10 hover:border-blue-500/30 transition-colors group"
              >
                <div className="w-7 h-7 rounded-full bg-blue-500/10 flex items-center justify-center text-[10px] font-bold text-blue-400">
                  {d.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate group-hover:text-blue-400 transition-colors">{d.name}</p>
                  <p className="text-[10px] text-muted-foreground">{d.position || d.position_group} — {d.matches} match(es)</p>
                </div>
                <span className="font-heading text-lg text-blue-400">{d.avg_score}</span>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {/* ROW 4: SQUAD + TEAMS PERFORMANCE */}
      <div className="grid lg:grid-cols-2 gap-3">
        {/* Squad Distribution */}
        <Card delay={0.5}>
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Users size={13} className="text-primary" /> Squad Distribution
          </h2>
          <div className="flex items-center gap-6">
            <div className="relative w-28 h-28">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                {totalEval > 0 ? (
                  <>
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#10b981" strokeWidth="3.5"
                      strokeDasharray={`${((dist.proficient || 0) / totalEval) * 100} ${100 - ((dist.proficient || 0) / totalEval) * 100}`}
                      strokeDashoffset="0" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f59e0b" strokeWidth="3.5"
                      strokeDasharray={`${((dist.developing || 0) / totalEval) * 100} ${100 - ((dist.developing || 0) / totalEval) * 100}`}
                      strokeDashoffset={`-${((dist.proficient || 0) / totalEval) * 100}`} />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#ef4444" strokeWidth="3.5"
                      strokeDasharray={`${((dist.focus || 0) / totalEval) * 100} ${100 - ((dist.focus || 0) / totalEval) * 100}`}
                      strokeDashoffset={`-${(((dist.proficient || 0) + (dist.developing || 0)) / totalEval) * 100}`} />
                  </>
                ) : (
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#3f3f46" strokeWidth="3.5" />
                )}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-heading text-lg text-primary">{totalEval}</span>
              </div>
            </div>
            <div className="space-y-2 flex-1">
              {[
                { label: 'Proficient', count: dist.proficient || 0, pct: totalEval > 0 ? Math.round(((dist.proficient || 0) / totalEval) * 100) : 0, color: 'bg-emerald-500', text: 'text-emerald-400' },
                { label: 'Developing', count: dist.developing || 0, pct: totalEval > 0 ? Math.round(((dist.developing || 0) / totalEval) * 100) : 0, color: 'bg-amber-500', text: 'text-amber-400' },
                { label: 'Focus', count: dist.focus || 0, pct: totalEval > 0 ? Math.round(((dist.focus || 0) / totalEval) * 100) : 0, color: 'bg-red-500', text: 'text-red-400' }
              ].map(d => (
                <div key={d.label} className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-sm ${d.color}`} />
                  <span className="text-xs flex-1">{d.label}</span>
                  <span className={`text-xs font-mono ${d.text}`}>{d.pct}%</span>
                  <span className="text-[10px] text-muted-foreground">({d.count})</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Teams Performance - Clickable */}
        <Card delay={0.55}>
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <BarChart3 size={13} className="text-primary" /> Teams Performance
          </h2>
          <div className="space-y-2">
            {(data.teams_summary || []).map(t => (
              <Link key={t.team_id} to={`/club/${club?.id}/teams/${t.team_id}`}
                className="flex items-center gap-3 p-2 bg-zinc-900/40 rounded-sm border border-border/10 hover:border-primary/20 transition-colors group"
                data-testid={`team-perf-${t.team_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate group-hover:text-primary transition-colors">{t.team_name}</p>
                  <p className="text-[10px] text-muted-foreground">{t.athlete_count} athletes · {t.category}</p>
                </div>
                <div className="w-20">
                  <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${(t.avg_score / 10) * 100}%` }}
                      className={`h-full rounded-full ${t.avg_score >= 8.1 ? 'bg-emerald-500' : t.avg_score >= 6.1 ? 'bg-amber-500' : 'bg-red-500'}`} />
                  </div>
                </div>
                <span className={`font-mono text-sm w-8 text-right ${t.avg_score >= 8.1 ? 'text-emerald-400' : t.avg_score >= 6.1 ? 'text-amber-400' : t.avg_score > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                  {t.avg_score || '—'}
                </span>
                <ChevronRight size={14} className="text-zinc-600 group-hover:text-primary transition-colors" />
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

const Card = ({ children, delay = 0, className = '' }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    className={`bg-card border border-border/40 rounded-sm p-4 ${className}`}
  >
    {children}
  </motion.div>
);

export default ClubDashboard;
